import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import { Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { useMonitoring } from '../../hooks/useMonitoring';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  status?: 'idle' | 'loading' | 'success' | 'error';
  errorMessage?: string;
  retryOnError?: boolean;
  onRetry?: () => void;
  confirmAction?: {
    message: string;
    confirmText?: string;
    cancelText?: string;
  };
  tooltip?: string;
  analytics?: {
    event: string;
    properties?: Record<string, any>;
  };
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      icon,
      iconPosition = 'left',
      status = 'idle',
      errorMessage,
      retryOnError = false,
      onRetry,
      confirmAction,
      tooltip,
      analytics,
      children,
      onClick,
      disabled,
      className = '',
      ...props
    },
    ref
  ) => {
    const { logEvent } = useMonitoring();
    const [showConfirm, setShowConfirm] = React.useState(false);
    const [localLoading, setLocalLoading] = React.useState(false);

    const isDisabled = disabled || loading || localLoading || status === 'loading';

    const getVariantClasses = () => {
      const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed';
      
      switch (variant) {
        case 'primary':
          return `${baseClasses} bg-blue-500 hover:bg-blue-600 focus:ring-blue-500 text-white shadow-sm hover:shadow-md`;
        case 'secondary':
          return `${baseClasses} bg-gray-600 hover:bg-gray-700 focus:ring-gray-500 text-white shadow-sm hover:shadow-md`;
        case 'danger':
          return `${baseClasses} bg-red-500 hover:bg-red-600 focus:ring-red-500 text-white shadow-sm hover:shadow-md`;
        case 'success':
          return `${baseClasses} bg-green-500 hover:bg-green-600 focus:ring-green-500 text-white shadow-sm hover:shadow-md`;
        case 'warning':
          return `${baseClasses} bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500 text-white shadow-sm hover:shadow-md`;
        case 'ghost':
          return `${baseClasses} bg-transparent hover:bg-gray-700 focus:ring-gray-500 text-gray-300 hover:text-white`;
        default:
          return baseClasses;
      }
    };

    const getSizeClasses = () => {
      switch (size) {
        case 'sm':
          return 'px-3 py-1.5 text-sm gap-1.5';
        case 'lg':
          return 'px-6 py-3 text-lg gap-3';
        default:
          return 'px-4 py-2 text-sm gap-2';
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case 'loading':
          return <Loader2 className="w-4 h-4 animate-spin" />;
        case 'success':
          return <CheckCircle className="w-4 h-4 text-green-400" />;
        case 'error':
          return <AlertTriangle className="w-4 h-4 text-red-400" />;
        default:
          return null;
      }
    };

    const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) return;

      // Handle confirmation dialog
      if (confirmAction && !showConfirm) {
        setShowConfirm(true);
        return;
      }

      // Log analytics event
      if (analytics) {
        logEvent(analytics.event, analytics.properties);
      }

      // Handle async operations
      if (onClick) {
        try {
          setLocalLoading(true);
          await onClick(e);
        } catch (error) {
          console.error('Button click error:', error);
          logEvent('button_click_error', {
            error: error instanceof Error ? error.message : 'Unknown error',
            buttonText: typeof children === 'string' ? children : 'Unknown',
            variant,
            size
          });
        } finally {
          setLocalLoading(false);
        }
      }

      setShowConfirm(false);
    };

    const handleRetry = () => {
      if (onRetry) {
        onRetry();
      }
    };

    const buttonContent = (
      <>
        {status === 'loading' || loading || localLoading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {loadingText || children}
          </>
        ) : (
          <>
            {iconPosition === 'left' && icon && icon}
            {getStatusIcon()}
            {children}
            {iconPosition === 'right' && icon && icon}
          </>
        )}
      </>
    );

    return (
      <div className="relative">
        <button
          ref={ref}
          onClick={handleClick}
          disabled={isDisabled}
          className={`${getVariantClasses()} ${getSizeClasses()} ${className}`}
          title={tooltip}
          aria-label={tooltip}
          {...props}
        >
          {buttonContent}
        </button>

        {/* Error message */}
        {status === 'error' && errorMessage && (
          <div className="absolute top-full left-0 mt-2 p-2 bg-red-500 text-white text-xs rounded-lg shadow-lg z-10 max-w-xs">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-3 h-3 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
            {retryOnError && onRetry && (
              <button
                onClick={handleRetry}
                className="mt-1 text-xs underline hover:no-underline"
              >
                Retry
              </button>
            )}
          </div>
        )}

        {/* Confirmation dialog */}
        {showConfirm && confirmAction && (
          <div className="absolute top-full left-0 mt-2 p-4 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 min-w-64">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-300 mb-3">{confirmAction.message}</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowConfirm(false)}
                    className="px-3 py-1.5 text-xs bg-gray-600 hover:bg-gray-700 rounded transition-colors"
                  >
                    {confirmAction.cancelText || 'Cancel'}
                  </button>
                  <button
                    onClick={handleClick}
                    className="px-3 py-1.5 text-xs bg-red-500 hover:bg-red-600 rounded transition-colors"
                  >
                    {confirmAction.confirmText || 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
);

Button.displayName = 'Button';

export default Button; 